import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, Sparkles, Calculator, Loader2, AlertCircle, CheckCircle, XCircle, Layers } from 'lucide-react';
import CalcSectionHeader from './CalcSectionHeader';
import { TRAY_SIZES, fieldDescriptions } from './constants';
import FieldInfo from './FieldInfo';
import { AuthContext } from '../../App';
import axios from 'axios';

const BatchBasics = ({
 showBatchBasics,
 setShowBatchBasics,
 selectedTraySize,
 handleTraySelection,
 numTrays,
 handleNumTrays,
 formData,
 setFormData,
 cycleTimeHours,
 setCycleTimeHours,
 askAIForCycleTime,
 aiCycleTimeEstimated,
 calculationMethod,
 setCalculationMethod,
 pieceSize,
 setPieceSize,
 piecesPerTray,
 setPiecesPerTray,
 piecesPerBag,
 setPiecesPerBag,
 totalBatchWeight,
 setTotalBatchWeight,
 waterContentPercent,
 setWaterContentPercent,
 askAIForWaterContent,
 aiLoading,
 wetWeightPerTray,
 setWetWeightPerTray,
 dryWeightPerTray,
 setDryWeightPerTray,
 quantityPerUnit,
 setQuantityPerUnit,
 unitType,
 setUnitType,
 askAIForSellingPrice,
 aiPriceExplanation
}) => {
 const { API } = useContext(AuthContext);
 const [freezeDryerModels, setFreezeDryerModels] = useState([]);
 const [selectedModel, setSelectedModel] = useState(null);
 const [loadingModels, setLoadingModels] = useState(true);

 // Fetch freeze dryer models on component mount
 useEffect(() => {
 const fetchModels = async () => {
 try {
 const response = await axios.get(`${API}/calculator/freeze-dryer-models`);
 setFreezeDryerModels(response.data.models || []);
 } catch (error) {
 console.error('Failed to fetch freeze dryer models:', error);
 } finally {
 setLoadingModels(false);
 }
 };
 fetchModels();
 }, [API]);

 // Restore selected model when formData has a saved model ID
 useEffect(() => {
 if (formData.freeze_dryer_model_id && freezeDryerModels.length > 0 && !selectedModel) {
 const savedModel = freezeDryerModels.find(m => m.id === formData.freeze_dryer_model_id);
 if (savedModel) {
 setSelectedModel(savedModel);
 // Update number of trays if saved
 if (savedModel.trays && !numTrays) {
 handleNumTrays(savedModel.trays.toString());
 }
 }
 }
 }, [formData.freeze_dryer_model_id, freezeDryerModels, selectedModel, numTrays, handleNumTrays]);

 // Handle model selection and auto-populate specs
 const handleModelSelection = (modelId) => {
 const model = freezeDryerModels.find(m => m.id === modelId);
 if (model) {
 setSelectedModel(model);
 
 // Auto-populate all specifications
 setFormData(prev => ({
 ...prev,
 freeze_dryer_model_id: model.id,
 freeze_dryer_model: model.display_name,
 max_batch_weight_lb: model.max_batch_weight_lb,
 max_batch_weight_kg: model.max_batch_weight_kg,
 max_batch_weight_oz: model.max_batch_weight_oz,
 max_batch_weight_g: model.max_batch_weight_g,
 tray_size: model.tray_size,
 total_tray_area_sqin: model.total_tray_area_sqin
 }));
 
 // Auto-populate number of trays
 handleNumTrays(model.trays.toString());
 }
 };

 // Calculate current batch weight from wet weight per tray
 const getCurrentBatchWeight = () => {
 // For weight-based calculation, use wet weight per tray × number of trays
 if (calculationMethod === 'weight' && wetWeightPerTray && numTrays) {
 const weightPerTray = parseFloat(wetWeightPerTray) || 0;
 const trays = parseFloat(numTrays) || 0;
 const totalWeight = weightPerTray * trays;
 const unit = formData.tray_weight_unit || 'lbs';
 return { weight: totalWeight, unit };
 }
 
 // For piece-based calculation, use total batch weight
 if (calculationMethod === 'pieces' && totalBatchWeight) {
 const weight = parseFloat(totalBatchWeight) || 0;
 const unit = formData.ingredient_weight_unit || 'lbs';
 return { weight, unit };
 }
 
 return null;
 };

 // Check if batch weight exceeds maximum
 const checkWeightLimit = () => {
 if (!selectedModel) return null;
 
 const currentBatch = getCurrentBatchWeight();
 if (!currentBatch) return null;
 
 const { weight, unit } = currentBatch;
 let maxWeight = 0;
 
 switch(unit) {
 case 'lbs':
 maxWeight = selectedModel.max_batch_weight_lb;
 break;
 case 'kg':
 maxWeight = selectedModel.max_batch_weight_kg;
 break;
 case 'oz':
 maxWeight = selectedModel.max_batch_weight_oz;
 break;
 case 'g':
 maxWeight = selectedModel.max_batch_weight_g;
 break;
 default:
 maxWeight = selectedModel.max_batch_weight_lb;
 }
 
 return {
 isOver: weight > maxWeight,
 currentWeight: weight.toFixed(2),
 maxWeight: maxWeight,
 unit: unit
 };
 };

 const weightCheck = checkWeightLimit();

 return (
 <Card className="border border-border rounded-2xl overflow-hidden">
 <CalcSectionHeader
 icon={Layers}
 title="Batch Basics"
 description="Machine, tray count and units per batch"
 expanded={showBatchBasics}
 onToggle={() => setShowBatchBasics(!showBatchBasics)}
 testId="section-header-batch"
 />
 {showBatchBasics && (
 <CardContent className="space-y-4 pt-5">
 {/* Simplified Freeze Dryer Model Selector */}
 <div className="bg-gradient-to-br from-blue-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg space-y-4 border-2 border-blue-200 dark:border-blue-800">
 <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">🔥 Select Your Freeze Dryer</p>
 
 <div className="space-y-3">
 <Label className="text-sm font-medium">Choose Your Model (Specs Auto-Fill!)</Label>
 
 {loadingModels ? (
 <div className="flex items-center justify-center p-4">
 <Loader2 className="w-5 h-5 animate-spin mr-2" />
 <span className="text-sm text-muted-foreground">Loading models...</span>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
 {freezeDryerModels.map(model => (
 <label 
 key={model.id} 
 className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-all ${
 selectedModel?.id === model.id 
 ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md' 
 : 'border-slate-200 dark:border-slate-700'
 }`}
 >
 <input
 type="radio"
 name="freeze-dryer-model"
 value={model.id}
 checked={selectedModel?.id === model.id}
 onChange={() => handleModelSelection(model.id)}
 className="w-4 h-4 mt-1"
 />
 <div className="flex-1">
 <span className="text-sm font-medium block">{model.display_name}</span>
 <span className="text-xs text-muted-foreground block mt-1">
 {model.trays} trays • {model.tray_size}" • Max: {model.max_batch_weight_lb} lbs
 </span>
 </div>
 </label>
 ))}
 </div>
 )}
 </div>

 {/* Auto-Populated Specifications */}
 {selectedModel && (
 <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border-2 border-green-300 dark:border-green-700 space-y-3">
 <p className="text-sm font-semibold text-green-900 dark:text-green-100 flex items-center gap-2">
 <CheckCircle className="w-4 h-4" />
 Specifications Auto-Loaded
 </p>
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div>
 <span className="text-muted-foreground">Number of Trays:</span>
 <span className="ml-2 font-bold">{selectedModel.trays}</span>
 </div>
 <div>
 <span className="text-muted-foreground">Tray Size:</span>
 <span className="ml-2 font-bold">{selectedModel.tray_size}"</span>
 </div>
 <div>
 <span className="text-muted-foreground">Total Tray Area:</span>
 <span className="ml-2 font-bold">{selectedModel.total_tray_area_sqin} sq in</span>
 </div>
 <div>
 <span className="text-muted-foreground">Max Capacity:</span>
 <span className="ml-2 font-bold text-blue-600">
 {selectedModel.max_batch_weight_lb} lbs / {selectedModel.max_batch_weight_kg} kg
 </span>
 </div>
 </div>

 {/* Weight Warning */}
 {weightCheck && (
 <div className={`p-3 rounded-lg border-2 ${
 weightCheck.isOver 
 ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700' 
 : 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700'
 }`}>
 <div className="flex items-center gap-2">
 {weightCheck.isOver ? (
 <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
 ) : (
 <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
 )}
 <div className="flex-1">
 <p className={`text-sm font-semibold ${
 weightCheck.isOver 
 ? 'text-red-900 dark:text-red-100' 
 : 'text-green-900 dark:text-green-100'
 }`}>
 {weightCheck.isOver ? '⚠️ Weight Limit Exceeded!' : '✓ Within Weight Limit'}
 </p>
 <p className={`text-xs mt-1 ${
 weightCheck.isOver 
 ? 'text-red-700 dark:text-red-300' 
 : 'text-green-700 dark:text-green-300'
 }`}>
 Current: {weightCheck.currentWeight} {weightCheck.unit} / Max: {weightCheck.maxWeight} {weightCheck.unit}
 </p>
 {weightCheck.isOver && (
 <p className="text-xs mt-2 text-red-800 dark:text-red-200 font-medium">
 Reduce batch weight or split into multiple batches
 </p>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 )}

 {/* Cycle Time */}
 <div className="pt-3 border-t border-blue-200 dark:border-blue-700 space-y-2">
 <div className="flex items-center justify-between">
 <Label className="text-sm font-medium">Cycle Time (hours per run)</Label>
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={askAIForCycleTime}
 disabled={aiLoading}
 className="h-7 text-xs"
 >
 {aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
 Ask AI
 </Button>
 </div>
 <Input
 type="number"
 step="0.5"
 value={cycleTimeHours}
 onChange={(e) => {
 setCycleTimeHours(e.target.value);
 setFormData(prev => ({ ...prev, cycle_time_hours: e.target.value }));
 }}
 placeholder="24"
 className={aiCycleTimeEstimated ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}
 />
 {aiCycleTimeEstimated && (
 <p className="text-xs text-blue-600 dark:text-blue-400">
 <Sparkles className="w-3 h-3 inline mr-1" />
 AI estimate based on your product
 </p>
 )}
 <p className="text-xs text-muted-foreground">
 Typical: 20-30 hours. Used for multi-day run planning & electricity costs
 </p>
 <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-200 dark:border-amber-800">
 <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
 <p>
 <strong>Note:</strong> Cycle time is an estimate. Actual times vary by product moisture, thickness, temperature, and machine. Always conduct your own research and testing for accurate results.
 </p>
 </div>
 </div>

 {/* Calculate by: Pieces or Weight */}
 <div className="pt-3 border-t border-blue-200 dark:border-blue-700 space-y-4">
 <div className="flex items-center gap-4">
 <Label className="text-sm font-semibold">Production Method:</Label>
 <div className="flex gap-4">
 <label className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${calculationMethod === 'pieces' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
 <input
 type="radio"
 name="calc-method"
 value="pieces"
 checked={calculationMethod === 'pieces'}
 onChange={() => setCalculationMethod('pieces')}
 className="w-4 h-4"
 />
 <span className="text-sm font-medium">🍬 Pieces (candy, items)</span>
 </label>
 <label className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${calculationMethod === 'weight' ? 'border-primary/30 bg-primary/5 dark:bg-primary/20/20' : 'border-slate-200 dark:border-slate-700'}`}>
 <input
 type="radio"
 name="calc-method"
 value="weight"
 checked={calculationMethod === 'weight'}
 onChange={() => setCalculationMethod('weight')}
 className="w-4 h-4"
 />
 <span className="text-sm font-medium">⚖️ Weight (bulk, produce)</span>
 </label>
 </div>
 </div>

 {calculationMethod === 'pieces' ? (
 <div className="space-y-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-2 border-blue-200 dark:border-blue-800">
 <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">🍬 Piece-Based Calculation</p>
 
 {/* Piece Size Estimator */}
 {selectedTraySize && (
 <div className="space-y-3 pb-3 border-b border-blue-200 dark:border-blue-700">
 <Label className="text-sm font-medium">Piece Size Estimator</Label>
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-2">
 <Label className="text-xs">Size per Piece (sq inches)</Label>
 <Input
 type="number"
 step="0.1"
 value={pieceSize}
 onChange={(e) => setPieceSize(e.target.value)}
 placeholder="2.0"
 />
 <p className="text-xs text-muted-foreground">Example: 1"×2" = 2 sq in</p>
 </div>
 </div>
 {piecesPerTray && (
 <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded text-sm">
 ✓ {piecesPerTray} pieces fit per tray
 </div>
 )}
 </div>
 )}

 {/* Pieces per Tray */}
 <div className="space-y-2">
 <Label className="text-sm">Pieces per Tray {pieceSize && selectedTraySize && <span className="text-green-600">✓ Auto-calculated</span>}</Label>
 <Input
 type="number"
 value={piecesPerTray}
 onChange={(e) => setPiecesPerTray(e.target.value)}
 placeholder="12"
 className={pieceSize && selectedTraySize ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}
 />
 <p className="text-xs text-muted-foreground">
 {pieceSize && selectedTraySize ? 'Calculated from tray size & piece size' : 'How many individual pieces fit on one tray'}
 </p>
 </div>

 {/* Pieces per Bag */}
 <div className="space-y-2">
 <Label className="text-sm">Pieces per Bag (Unit)</Label>
 <Input
 type="number"
 value={piecesPerBag}
 onChange={(e) => setPiecesPerBag(e.target.value)}
 placeholder="4"
 />
 <p className="text-xs text-muted-foreground">
 How many pieces per sellable unit/bag
 </p>
 </div>

 {/* Max Pieces Calculator */}
 <div className="bg-gradient-to-r from-blue-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-3 rounded-lg border-2 border-blue-300 dark:border-blue-700">
 <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">📊 Max Pieces in Run</p>
 <div className="bg-white dark:bg-slate-800 p-3 rounded">
 <p className="text-2xl font-bold text-blue-600">
 {(parseFloat(piecesPerTray) || 0) * (parseFloat(numTrays) || 0)} pieces
 </p>
 <p className="text-xs text-muted-foreground mt-1">
 {piecesPerTray} pieces/tray × {numTrays} trays
 </p>
 {piecesPerBag && parseFloat(piecesPerBag) > 0 && (
 <p className="text-sm font-medium text-green-600 mt-2">
 = {Math.floor(((parseFloat(piecesPerTray) || 0) * (parseFloat(numTrays) || 0)) / parseFloat(piecesPerBag))} units/bags
 </p>
 )}
 </div>
 </div>

 {/* Batch Weight Calculator Helper */}
 <div className="space-y-3 pt-3 pb-3 border-t border-blue-200 dark:border-blue-700 bg-pro-soft to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-3 rounded-lg">
 <Label className="text-sm font-semibold text-cyan-900 dark:text-cyan-100">🧮 Batch Weight Helper</Label>
 <p className="text-xs text-muted-foreground">Calculate total batch weight from a sample measurement</p>
 
 <div className="grid grid-cols-2 gap-3">
 <div className="space-y-2">
 <Label className="text-xs">Sample Quantity (pieces)</Label>
 <Input
 type="number"
 placeholder="e.g., 10"
 onChange={(e) => {
 const sampleQty = parseFloat(e.target.value) || 0;
 const sampleWeight = parseFloat(e.target.parentElement.parentElement.querySelector('[data-sample-weight]')?.value) || 0;
 const totalPieces = (parseFloat(piecesPerTray) || 0) * (parseFloat(numTrays) || 0);
 if (sampleQty > 0 && sampleWeight > 0 && totalPieces > 0) {
 const weightPerPiece = sampleWeight / sampleQty;
 const calculatedTotal = weightPerPiece * totalPieces;
 setTotalBatchWeight(calculatedTotal.toFixed(2));
 }
 }}
 className="h-8 text-sm"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-xs">Sample Weight</Label>
 <Input
 type="number"
 step="0.1"
 placeholder="e.g., 25g"
 data-sample-weight
 onChange={(e) => {
 const sampleWeight = parseFloat(e.target.value) || 0;
 const sampleQty = parseFloat(e.target.parentElement.parentElement.querySelector('input[type="number"]')?.value) || 0;
 const totalPieces = (parseFloat(piecesPerTray) || 0) * (parseFloat(numTrays) || 0);
 if (sampleQty > 0 && sampleWeight > 0 && totalPieces > 0) {
 const weightPerPiece = sampleWeight / sampleQty;
 const calculatedTotal = weightPerPiece * totalPieces;
 setTotalBatchWeight(calculatedTotal.toFixed(2));
 }
 }}
 className="h-8 text-sm"
 />
 </div>
 </div>
 <p className="text-xs text-cyan-700 dark:text-cyan-300">
 💡 Example: "10 pieces weigh 25g" → Calculates total for all {(parseFloat(piecesPerTray) || 0) * (parseFloat(numTrays) || 0)} pieces
 </p>
 </div>

 {/* Total Batch Weight with Unit Selector */}
 <div className="space-y-2">
 <Label className="text-sm font-medium">
 Total Batch Weight <span className="text-red-500">*</span>
 </Label>
 <div className="flex gap-2">
 <Input
 type="number"
 step="0.1"
 value={totalBatchWeight}
 onChange={(e) => setTotalBatchWeight(e.target.value)}
 placeholder="100"
 className="flex-1"
 />
 <select
 value={formData.ingredient_weight_unit || 'lbs'}
 onChange={(e) => setFormData(prev => ({ ...prev, ingredient_weight_unit: e.target.value }))}
 className="w-24 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm"
 >
 <option value="lbs">lbs</option>
 <option value="kg">kg</option>
 <option value="g">g</option>
 <option value="oz">oz</option>
 </select>
 </div>
 <p className="text-xs text-muted-foreground">
 💡 Total weight of ALL pieces in the entire batch (all trays combined)
 </p>
 </div>

 {/* Bag Weight Calculator Helper */}
 {piecesPerBag && totalBatchWeight && (
 <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded border border-green-300 dark:border-green-700">
 <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">📦 Calculated Bag Weight</p>
 <p className="text-lg font-bold text-green-600">
 {(parseFloat(totalBatchWeight) / ((parseFloat(piecesPerTray) || 1) * (parseFloat(numTrays) || 1)) * parseFloat(piecesPerBag)).toFixed(2)} {formData.ingredient_weight_unit || 'lbs'}
 </p>
 <p className="text-xs text-muted-foreground mt-1">
 {piecesPerBag} pieces per bag × {(parseFloat(totalBatchWeight) / ((parseFloat(piecesPerTray) || 1) * (parseFloat(numTrays) || 1))).toFixed(3)} {formData.ingredient_weight_unit || 'lbs'}/piece
 </p>
 </div>
 )}
 </div>
 ) : (
 <div className="space-y-4 bg-primary/5 dark:bg-primary/20/20 p-4 rounded-lg border-2 border-primary/30 dark:border-primary/30">
 <p className="text-sm font-semibold text-primary dark:text-primary">⚖️ Weight-Based Calculation</p>
 
 {/* Water Content AI Estimator */}
 <div className="space-y-2 pb-3 border-b border-primary/30 dark:border-primary/30">
 <div className="flex items-center justify-between">
 <Label className="text-sm font-medium">Water Content %</Label>
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={askAIForWaterContent}
 disabled={aiLoading}
 className="h-7 text-xs"
 >
 {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
 Ask AI
 </Button>
 </div>
 <Input
 type="number"
 step="0.1"
 value={waterContentPercent}
 onChange={(e) => setWaterContentPercent(e.target.value)}
 placeholder="85"
 />
 <p className="text-xs text-muted-foreground">
 AI estimates water % based on your product
 </p>
 </div>

 {/* Weight Unit Selector */}
 <div className="space-y-2 pb-2 border-b border-primary/30 dark:border-primary/30">
 <Label className="text-xs font-semibold">Weight Unit</Label>
 <div className="grid grid-cols-4 gap-2">
 {['lbs', 'oz', 'kg', 'g'].map(unit => (
 <label key={unit} className={`flex items-center justify-center gap-1 p-2 border rounded cursor-pointer text-xs ${formData.tray_weight_unit === unit ? 'border-primary/30 bg-primary/5 dark:bg-primary/20/40' : 'border-slate-200 dark:border-slate-700'}`}>
 <input
 type="radio"
 name="tray-weight-unit"
 checked={formData.tray_weight_unit === unit}
 onChange={() => setFormData(prev => ({ ...prev, tray_weight_unit: unit }))}
 className="w-3 h-3"
 />
 <span>{unit}</span>
 </label>
 ))}
 </div>
 </div>

 {/* Wet Weight per Tray */}
 <div className="space-y-2">
 <Label className="text-sm">Wet Weight per Tray ({formData.tray_weight_unit || 'lbs'})</Label>
 <Input
 type="number"
 step="0.1"
 value={wetWeightPerTray}
 onChange={(e) => setWetWeightPerTray(e.target.value)}
 placeholder="3.0"
 />
 <p className="text-xs text-muted-foreground">
 Weight BEFORE freeze-drying
 </p>
 </div>

 {/* Dry Weight per Tray */}
 <div className="space-y-2">
 <Label className="text-sm">Dry Weight per Tray ({formData.tray_weight_unit || 'lbs'}) <span className="text-green-600">✓ Auto-calculated</span></Label>
 <Input
 type="number"
 step="0.01"
 value={dryWeightPerTray}
 onChange={(e) => setDryWeightPerTray(e.target.value)}
 placeholder="0.45"
 className={waterContentPercent && wetWeightPerTray ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}
 />
 <p className="text-xs text-muted-foreground">
 Weight AFTER freeze-drying (calculates automatically)
 </p>
 </div>

 {/* Weight per Bag/Unit with Converter */}
 <div className="space-y-3 pt-3 border-t border-primary/30 dark:border-primary/30">
 <Label className="text-sm font-medium">Weight per Bag/Unit</Label>
 <div className="grid grid-cols-3 gap-2">
 <div className="space-y-1">
 <Label className="text-xs">Pounds</Label>
 <Input
 type="number"
 step="0.01"
 value={quantityPerUnit && unitType === 'pounds' ? quantityPerUnit : ''}
 onChange={(e) => {
 setUnitType('pounds');
 setQuantityPerUnit(e.target.value);
 }}
 placeholder="0.25"
 />
 </div>
 <div className="space-y-1">
 <Label className="text-xs">Ounces</Label>
 <Input
 type="number"
 step="0.1"
 value={quantityPerUnit && unitType === 'ounces' ? quantityPerUnit : (quantityPerUnit && unitType === 'pounds' ? (parseFloat(quantityPerUnit) * 16).toFixed(1) : '')}
 onChange={(e) => {
 setUnitType('ounces');
 setQuantityPerUnit(e.target.value);
 }}
 placeholder="4"
 />
 </div>
 <div className="space-y-1">
 <Label className="text-xs">Grams</Label>
 <Input
 type="number"
 step="1"
 value={quantityPerUnit && unitType === 'grams' ? quantityPerUnit : (quantityPerUnit && unitType === 'pounds' ? (parseFloat(quantityPerUnit) * 453.592).toFixed(0) : '')}
 onChange={(e) => {
 setUnitType('grams');
 setQuantityPerUnit(e.target.value);
 }}
 placeholder="113"
 />
 </div>
 </div>
 <p className="text-xs text-muted-foreground">
 💡 Enter in any unit - others auto-convert. Example: 4oz = 0.25 lbs = 113g
 </p>
 </div>

 {/* Total Production Summary */}
 <div className="space-y-3 pt-3 border-t border-primary/30 dark:border-primary/30">
 <p className="text-sm font-semibold text-primary dark:text-primary">📊 Total Per Run Summary</p>
 
 <div className="grid grid-cols-3 gap-3">
 <div className="bg-white dark:bg-slate-800 p-3 rounded border border-primary/30 dark:border-primary/30">
 <Label className="text-xs text-muted-foreground">Total Wet Weight</Label>
 <p className="text-lg font-bold text-primary">
 {((parseFloat(wetWeightPerTray) || 0) * (parseFloat(numTrays) || 0)).toFixed(2)} lbs
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded border border-orange-200 dark:border-orange-700">
 <Label className="text-xs text-muted-foreground">Expected Water Loss</Label>
 <p className="text-lg font-bold text-orange-600">
 {(((parseFloat(wetWeightPerTray) || 0) * (parseFloat(numTrays) || 0)) - ((parseFloat(dryWeightPerTray) || 0) * (parseFloat(numTrays) || 0))).toFixed(2)} lbs
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded border border-green-200 dark:border-green-700">
 <Label className="text-xs text-muted-foreground">Total Dry Weight</Label>
 <p className="text-lg font-bold text-green-600">
 {((parseFloat(dryWeightPerTray) || 0) * (parseFloat(numTrays) || 0)).toFixed(2)} lbs
 </p>
 </div>
 </div>

 {quantityPerUnit && (
 <div className="bg-gradient-to-r to-green-50 dark:/20 dark:to-green-900/20 p-3 rounded border border-primary/30 dark:border-primary/30">
 <p className="text-sm font-medium">Units per Batch:</p>
 <p className="text-2xl font-bold text-primary">
 {Math.floor(((parseFloat(dryWeightPerTray) || 0) * (parseFloat(numTrays) || 0)) / (unitType === 'pounds' ? parseFloat(quantityPerUnit) : unitType === 'ounces' ? parseFloat(quantityPerUnit) / 16 : parseFloat(quantityPerUnit) / 453.592))} units
 </p>
 <p className="text-xs text-muted-foreground mt-1">
 Based on {quantityPerUnit} {unitType} per unit
 </p>
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 </div>

 <div className="space-y-2">
 <Label>
 Units per Batch *
 {fieldDescriptions.units_per_batch && <FieldInfo description={fieldDescriptions.units_per_batch} />}
 </Label>
 <Input
 type="number"
 value={formData.units_per_batch}
 onChange={(e) => setFormData(prev => ({ ...prev, units_per_batch: e.target.value }))}
 placeholder="50"
 data-testid="input-units_per_batch"
 />
 <p className="text-xs text-muted-foreground">
 Calculated automatically from production method above
 </p>
 </div>

 <div className="space-y-2">
 <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded border border-green-200 dark:border-green-800 space-y-2">
 <div className="flex items-center justify-between">
 <Label className="text-sm font-medium">Selling Price per Unit ($) *</Label>
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={askAIForSellingPrice}
 disabled={aiLoading}
 className="h-7 text-xs"
 >
 {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
 Ask AI
 </Button>
 </div>
 <Input
 type="number"
 step="0.01"
 value={formData.selling_price}
 onChange={(e) => setFormData(prev => ({ ...prev, selling_price: e.target.value }))}
 placeholder="15.00"
 data-testid="input-selling_price"
 className={formData.selling_price && aiPriceExplanation ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}
 />
 
 {aiPriceExplanation && (
 <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-200 dark:border-blue-800 text-xs">
 <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">AI Research:</p>
 <p className="text-blue-800 dark:text-blue-200">{aiPriceExplanation}</p>
 </div>
 )}

 <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-200 dark:border-amber-800">
 <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
 <p>
 <strong>Disclaimer:</strong> This is an estimated look-up figure based on web search. Please conduct your own market research for more specific information about your local market, competitors, and customer demand.
 </p>
 </div>
 </div>
 </div>
 </CardContent>
 )}
 </Card>
 );
};

export default BatchBasics;
